import React, { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { createChart, ColorType, CrosshairMode, LineStyle } from 'lightweight-charts';
import { ScreenerStockResponse } from '../../services/screenerApi.ts';
import './StockChartWithDrawings.css';

// ── Types ─────────────────────────────────────────────────────────────────────

type DrawingTool = 'CURSOR' | 'TREND_LINE' | 'ARROW' | 'H_LINE' | 'V_LINE' | 'H_RAY_RIGHT' | 'H_RAY_LEFT' | 'RECTANGLE' | 'FREEHAND' | 'ARC' | 'TEXT';

interface ChartPoint { time: string; price: number; }

interface Drawing {
  id: string;
  type: DrawingTool;
  points: ChartPoint[];
  color: string;
  lineWidth: number;
  text?: string;
  meta?: Record<string, unknown>;
}

// ── Arc helper ────────────────────────────────────────────────────────────────

function drawEllipseArc(
  ctx: CanvasRenderingContext2D,
  p0: { x: number; y: number },   // left endpoint (pixels)
  p1: { x: number; y: number },   // right endpoint (pixels)
  apex: { x: number; y: number }, // apex point (pixels)
) {
  const adx = p1.x - p0.x;
  const ady = p1.y - p0.y;
  const chordLen = Math.hypot(adx, ady);
  if (chordLen < 1) return;
  const perpX = -ady / chordLen;
  const perpY =  adx / chordLen;
  const midX = (p0.x + p1.x) / 2;
  const midY = (p0.y + p1.y) / 2;
  const h = (apex.x - midX) * perpX + (apex.y - midY) * perpY;
  if (Math.abs(h) < 0.5) return;
  ctx.ellipse(midX, midY, chordLen / 2, Math.abs(h), Math.atan2(ady, adx), Math.PI, 0, h < 0);
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  stock: ScreenerStockResponse;
  drawings: Drawing[];
  onClose: () => void;
  isAIScreener?: boolean;
}

// ── Component ─────────────────────────────────────────────────────────────────

const StockChartWithDrawings: React.FC<Props> = ({ stock, drawings, onClose, isAIScreener = false }) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const canvasRef         = useRef<HTMLCanvasElement>(null);
  const chartRef          = useRef<any>(null);
  const seriesRef         = useRef<any>(null);
  const drawingsRef       = useRef<Drawing[]>(drawings);
  drawingsRef.current     = drawings;

  const [priceData, setPriceData]   = useState({ open: 0, high: 0, low: 0, close: 0, prevClose: 0 });
  const [isDarkMode, setIsDarkMode] = useState(() => document.body.classList.contains('dark-theme'));

  // ── Measured Move state ───────────────────────────────────────────────────
  const [showMMPanel, setShowMMPanel] = useState(false);
  const [mmEnabled, setMMEnabled]     = useState<number[]>([1, 2]);
  const mmLinesRef                    = useRef<Map<number, any>>(new Map());
  const [chartVersion, setChartVersion] = useState(0);

  // Keys are reward ratios: 1:1 → key 1, 1:2 → key 2, 1:3 → key 3
  const MM_COLORS: Record<number, string> = { 1: '#00c853', 2: '#a855f7', 3: '#ff6d00' };
  const MM_REWARDS = [1, 2, 3];

  const mmData = useMemo(() => {
    if (stock.resistanceLevel == null || stock.supportLevel == null) return null;
    // Always use the higher value as reference, lower as base — handles inverted backend data
    const entry    = Math.max(stock.resistanceLevel, stock.supportLevel);
    const stopLoss = Math.min(stock.resistanceLevel, stock.supportLevel);
    const risk     = entry - stopLoss;
    if (risk === 0) return null;
    return {
      entry,
      stopLoss,
      risk,
      levels: MM_REWARDS.map(reward => ({
        reward,
        price: +(entry + risk * reward).toFixed(2),
        pct:   +((risk * reward / entry) * 100).toFixed(1),
      })),
    };
  }, [stock.resistanceLevel, stock.supportLevel]);

  const canShowMM = isAIScreener;

  const toggleMMLine = (reward: number) =>
    setMMEnabled(prev => prev.includes(reward) ? prev.filter(r => r !== reward) : [...prev, reward]);

  // Sync MM price lines on chart whenever state or chart version changes
  useEffect(() => {
    if (!seriesRef.current || !mmData) return;
    MM_REWARDS.forEach(reward => {
      const existing = mmLinesRef.current.get(reward);
      if (mmEnabled.includes(reward)) {
        if (!existing) {
          const lvl  = mmData.levels.find(l => l.reward === reward)!;
          const line = seriesRef.current.createPriceLine({
            price: lvl.price, color: MM_COLORS[reward],
            lineWidth: 1, lineStyle: LineStyle.Dashed,
            axisLabelVisible: true, title: `${reward}× Ext`,
          });
          mmLinesRef.current.set(reward, line);
        }
      } else if (existing) {
        try { seriesRef.current.removePriceLine(existing); } catch {}
        mmLinesRef.current.delete(reward);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mmEnabled, mmData, chartVersion]);

  // Track dark/light mode
  useEffect(() => {
    const obs = new MutationObserver(() => setIsDarkMode(document.body.classList.contains('dark-theme')));
    obs.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  const toPixel = useCallback((time: string, price: number) => {
    if (!chartRef.current || !seriesRef.current) return null;
    const x = chartRef.current.timeScale().timeToCoordinate(time);
    const y = seriesRef.current.priceToCoordinate(price);
    if (x === null || y === null) return null;
    return { x: Math.round(x), y: Math.round(y) };
  }, []);

  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const psWidth = (chartRef.current?.priceScale('right').width?.() ?? 0);
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, canvas.width - psWidth, canvas.height);
    ctx.clip();

    drawingsRef.current.forEach(d => {
      ctx.strokeStyle = d.color;
      ctx.lineWidth   = Math.max(1, d.lineWidth);
      ctx.globalAlpha = 1;
      ctx.setLineDash([]);

      switch (d.type) {
        case 'H_LINE': {
          const y = seriesRef.current?.priceToCoordinate(d.points[0].price);
          if (y === null || y === undefined) break;
          ctx.beginPath();
          ctx.moveTo(0, Math.round(y));
          ctx.lineTo(canvas.width, Math.round(y));
          ctx.stroke();
          break;
        }
        case 'H_RAY_RIGHT': {
          const y = seriesRef.current?.priceToCoordinate(d.points[0].price);
          const x = chartRef.current?.timeScale().timeToCoordinate(d.points[0].time);
          if (y == null || x == null) break;
          ctx.beginPath();
          ctx.moveTo(Math.round(x), Math.round(y));
          ctx.lineTo(canvas.width, Math.round(y));
          ctx.stroke();
          break;
        }
        case 'H_RAY_LEFT': {
          const y = seriesRef.current?.priceToCoordinate(d.points[0].price);
          const x = chartRef.current?.timeScale().timeToCoordinate(d.points[0].time);
          if (y == null || x == null) break;
          ctx.beginPath();
          ctx.moveTo(0, Math.round(y));
          ctx.lineTo(Math.round(x), Math.round(y));
          ctx.stroke();
          break;
        }
        case 'V_LINE': {
          const x = chartRef.current?.timeScale().timeToCoordinate(d.points[0].time);
          if (x === null || x === undefined) break;
          ctx.beginPath();
          ctx.moveTo(Math.round(x), 0);
          ctx.lineTo(Math.round(x), canvas.height);
          ctx.stroke();
          break;
        }
        case 'TEXT': {
          const tx = chartRef.current?.timeScale().timeToCoordinate(d.points[0].time);
          const ty = seriesRef.current?.priceToCoordinate(d.points[0].price);
          if (tx == null || ty == null) break;
          ctx.fillStyle    = d.color;
          ctx.font         = `bold ${10 + d.lineWidth * 3}px sans-serif`;
          ctx.textBaseline = 'middle';
          ctx.fillText(d.text ?? '', Math.round(tx), Math.round(ty));
          break;
        }
        default: {
          const pts = d.points.map(p => toPixel(p.time, p.price)).filter(Boolean) as { x: number; y: number }[];

          switch (d.type) {
            case 'TREND_LINE': {
              if (pts.length < 2) break;
              ctx.beginPath(); ctx.moveTo(pts[0].x, pts[0].y); ctx.lineTo(pts[1].x, pts[1].y); ctx.stroke();
              break;
            }
            case 'ARROW': {
              if (pts.length < 2) break;
              const [p0, p1] = pts;
              const angle   = Math.atan2(p1.y - p0.y, p1.x - p0.x);
              const headLen = Math.max(12, d.lineWidth * 5);
              ctx.beginPath(); ctx.moveTo(p0.x, p0.y); ctx.lineTo(p1.x, p1.y); ctx.stroke();
              ctx.beginPath();
              ctx.moveTo(p1.x, p1.y);
              ctx.lineTo(p1.x - headLen * Math.cos(angle - Math.PI / 6), p1.y - headLen * Math.sin(angle - Math.PI / 6));
              ctx.lineTo(p1.x - headLen * Math.cos(angle + Math.PI / 6), p1.y - headLen * Math.sin(angle + Math.PI / 6));
              ctx.closePath(); ctx.fillStyle = d.color; ctx.fill();
              break;
            }
            case 'RECTANGLE': {
              if (pts.length < 2) break;
              const rx = Math.min(pts[0].x, pts[1].x), ry = Math.min(pts[0].y, pts[1].y);
              const rw = Math.abs(pts[1].x - pts[0].x), rh = Math.abs(pts[1].y - pts[0].y);
              ctx.strokeRect(rx, ry, rw, rh);
              ctx.save(); ctx.globalAlpha = 0.13; ctx.fillStyle = d.color; ctx.fillRect(rx, ry, rw, rh); ctx.restore();
              break;
            }
            case 'ARC': {
              if (pts.length < 3) break;
              ctx.beginPath(); drawEllipseArc(ctx, pts[0], pts[1], pts[2]); ctx.closePath();
              ctx.save(); ctx.globalAlpha = 0.13; ctx.fillStyle = d.color; ctx.fill(); ctx.restore();
              ctx.beginPath(); drawEllipseArc(ctx, pts[0], pts[1], pts[2]); ctx.stroke();
              break;
            }
            case 'FREEHAND': {
              if (pts.length < 2) break;
              ctx.lineJoin = 'round'; ctx.lineCap = 'round';
              ctx.beginPath(); ctx.moveTo(pts[0].x, pts[0].y);
              for (let i = 1; i < pts.length - 1; i++) {
                const mx = (pts[i].x + pts[i + 1].x) / 2;
                const my = (pts[i].y + pts[i + 1].y) / 2;
                ctx.quadraticCurveTo(pts[i].x, pts[i].y, mx, my);
              }
              ctx.lineTo(pts[pts.length - 1].x, pts[pts.length - 1].y);
              ctx.stroke();
              break;
            }
          }
        }
      }
    });

    ctx.restore();
  }, [toPixel]);

  const redrawRef = useRef(redrawCanvas);
  redrawRef.current = redrawCanvas;

  useEffect(() => {
    if (!chartContainerRef.current || !stock.candles?.length) return;
    const container = chartContainerRef.current;

    const gridColor  = isDarkMode ? 'rgba(197,203,206,0.1)' : 'rgba(0,0,0,0.07)';
    const scaleColor = isDarkMode ? 'rgba(197,203,206,0.3)' : 'rgba(0,0,0,0.15)';
    const textColor  = isDarkMode ? '#d1d4dc' : '#333';

    const chart = createChart(container, {
      autoSize: true,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor,
      },
      grid: {
        vertLines: { color: gridColor },
        horzLines: { color: gridColor },
      },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: scaleColor },
      timeScale: { borderColor: scaleColor, rightOffset: 8, barSpacing: 8 },
    });
    chartRef.current = chart;

    const series = (chart as any).addCandlestickSeries({
      upColor: '#26a69a', downColor: '#ef5350', borderVisible: false,
      wickUpColor: '#26a69a', wickDownColor: '#ef5350',
    });
    seriesRef.current = series;

    const chartData = stock.candles!.map(c => ({ time: c.time, open: c.open, high: c.high, low: c.low, close: c.close }));
    series.setData(chartData);

    if (isAIScreener) {
      if (stock.resistanceLevel != null || stock.supportLevel != null) {
        const resistanceLevel = stock.resistanceLevel;
        const supportLevel = stock.supportLevel;
        series.applyOptions({
          autoscaleInfoProvider: (original: () => any) => {
            const res = original();
            if (!res || !res.priceRange) return res;
            let { minValue, maxValue } = res.priceRange;
            if (resistanceLevel != null) { minValue = Math.min(minValue, resistanceLevel); maxValue = Math.max(maxValue, resistanceLevel); }
            if (supportLevel    != null) { minValue = Math.min(minValue, supportLevel);    maxValue = Math.max(maxValue, supportLevel);    }
            return { ...res, priceRange: { minValue, maxValue } };
          },
        });
      }
      if (stock.resistanceLevel != null) {
        series.createPriceLine({ price: stock.resistanceLevel, color: '#f59e0b', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: 'Resistance' });
      }
      if (stock.supportLevel != null) {
        series.createPriceLine({ price: stock.supportLevel, color: '#3b82f6', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: 'Support' });
      }
    }

    const vol = (chart as any).addHistogramSeries({ priceFormat: { type: 'volume' }, priceScaleId: '' });
    vol.priceScale().applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });
    vol.setData(stock.candles!.map(c => ({
      time: c.time, value: c.volume || 0,
      color: c.close > c.open ? 'rgba(38,166,154,0.5)' : 'rgba(239,83,80,0.5)',
    })));

    // Seed OHLC with last candle
    const last = chartData[chartData.length - 1];
    const prev = chartData[chartData.length - 2];
    setPriceData({ open: last.open, high: last.high, low: last.low, close: last.close, prevClose: prev?.close ?? last.close });

    // Update OHLC on crosshair move
    chart.subscribeCrosshairMove((param: any) => {
      if (!param?.time) {
        setPriceData({ open: last.open, high: last.high, low: last.low, close: last.close, prevClose: prev?.close ?? last.close });
        return;
      }
      if (param.seriesData?.size > 0) {
        const d = param.seriesData.values().next().value;
        if (d?.open !== undefined) {
          const i = chartData.findIndex((c: any) => c.time === param.time);
          setPriceData({ open: d.open, high: d.high, low: d.low, close: d.close, prevClose: i > 0 ? chartData[i - 1].close : d.close });
        }
      }
    });

    const syncCanvas = () => {
      const rect = container.getBoundingClientRect();
      const cw = Math.round(rect.width)  || container.clientWidth;
      const ch = Math.round(rect.height) || container.clientHeight;
      if (canvasRef.current) { canvasRef.current.width = cw; canvasRef.current.height = ch; }
    };
    syncCanvas();

    chart.timeScale().subscribeVisibleLogicalRangeChange(() => redrawRef.current());
    chart.timeScale().subscribeVisibleTimeRangeChange(()   => redrawRef.current());
    const onMove = () => redrawRef.current();
    container.addEventListener('mousemove', onMove);

    const ro = new ResizeObserver(() => { syncCanvas(); redrawRef.current(); });
    ro.observe(container);

    // Notify MM effect that a fresh series is ready
    setChartVersion(v => v + 1);

    return () => {
      mmLinesRef.current.clear();
      ro.disconnect();
      container.removeEventListener('mousemove', onMove);
      chart.remove();
      chartRef.current  = null;
      seriesRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stock.id, isDarkMode]);

  useEffect(() => { redrawCanvas(); }, [redrawCanvas, drawings]);

  const change        = priceData.close - priceData.prevClose;
  const changePercent = priceData.prevClose > 0 ? (change / priceData.prevClose) * 100 : 0;
  const isPositive    = change >= 0;
  const isBullish     = priceData.close >= priceData.open;
  const tf = stock.timeframe === 'Daily' ? 'D' : stock.timeframe === 'Weekly' ? 'W' : stock.timeframe === 'Monthly' ? 'M' : (stock.timeframe ?? '');

  return (
    <div className="scd-overlay" onClick={onClose}>
      <div className="scd-modal" onClick={e => e.stopPropagation()}>

        {/* Header row 1: symbol + close */}
        <div className="scd-header">
          <div className="scd-title">
            <span className="scd-symbol">{stock.symbol}</span>
            {tf && <span className="scd-tf">{tf}</span>}
            {stock.exchange && <span className="scd-exchange">{stock.exchange}</span>}
            {stock.capturedPrice != null && (
              <span className="scd-cap-price">₹{stock.capturedPrice.toFixed(2)}</span>
            )}
          </div>

          {/* OHLC */}
          {stock.candles?.length ? (
            <div className="scd-ohlc">
              <span className="scd-pp"><span className="scd-pl">O</span><span className={`scd-pv ${isBullish ? 'bull' : 'bear'}`}>{priceData.open.toFixed(2)}</span></span>
              <span className="scd-pp"><span className="scd-pl">H</span><span className={`scd-pv ${isBullish ? 'bull' : 'bear'}`}>{priceData.high.toFixed(2)}</span></span>
              <span className="scd-pp"><span className="scd-pl">L</span><span className={`scd-pv ${isBullish ? 'bull' : 'bear'}`}>{priceData.low.toFixed(2)}</span></span>
              <span className="scd-pp"><span className="scd-pl">C</span><span className={`scd-pv ${isBullish ? 'bull' : 'bear'}`}>{priceData.close.toFixed(2)}</span></span>
              <span className={`scd-change ${isPositive ? 'pos' : 'neg'}`}>
                {isPositive ? '+' : ''}{change.toFixed(2)} ({isPositive ? '+' : ''}{changePercent.toFixed(2)}%)
              </span>
            </div>
          ) : null}

          {canShowMM && (
            <>
              <div className="scd-sep" />
              <button
                className={`scd-mm-btn${showMMPanel ? ' active' : ''}`}
                onClick={() => setShowMMPanel(p => !p)}
                title="Measured Move"
              >
                <span className="scd-mm-btn-icon">📐</span>
                <span className="scd-mm-btn-label">Measured Move</span>
              </button>
            </>
          )}

          <button className="scd-close" onClick={onClose}>✕</button>
        </div>

        {!stock.candles?.length ? (
          <div className="scd-no-data">No chart data available for {stock.symbol}</div>
        ) : (
          <div className="scd-chart-area">
            <div ref={chartContainerRef} className="scd-chart-container" />
            <canvas ref={canvasRef} className="scd-drawing-canvas" />

            {/* ── Measured Move Panel ─────────────────────────────────── */}
            {showMMPanel && (
              <div className="scd-mm-panel">
                <div className="scd-mm-panel-header scd-mm-panel-header--sticky">
                  <span className="scd-mm-panel-title">📐 Measured Move</span>
                  <button className="scd-mm-panel-close" onClick={() => setShowMMPanel(false)}>✕</button>
                </div>
                <div className="scd-mm-panel-body">

                {mmData ? (
                  <>
                    <div className="scd-mm-info">
                      <div className="scd-mm-info-row">
                        <span className="scd-mm-info-label">Resistance</span>
                        <span className="scd-mm-info-val">₹{mmData.entry.toFixed(2)}</span>
                      </div>
                      <div className="scd-mm-info-row">
                        <span className="scd-mm-info-label">Support</span>
                        <span className="scd-mm-info-val" style={{ color: '#3b82f6' }}>₹{mmData.stopLoss.toFixed(2)}</span>
                      </div>
                      <div className="scd-mm-info-row">
                        <span className="scd-mm-info-label">Range</span>
                        <span className="scd-mm-info-val">₹{mmData.risk.toFixed(2)}</span>
                      </div>
                    </div>

                    <div className="scd-mm-ext-label">Extensions</div>

                    {mmData.levels.map(({ reward, price, pct }) => {
                      const on = mmEnabled.includes(reward);
                      return (
                        <div
                          key={reward}
                          className={`scd-mm-ext-row${on ? ' on' : ''}`}
                          style={{ '--mm-color': MM_COLORS[reward] } as React.CSSProperties}
                          onClick={() => toggleMMLine(reward)}
                        >
                          <span className="scd-mm-dot" style={{ background: MM_COLORS[reward] }} />
                          <div className="scd-mm-ext-body">
                            <span className="scd-mm-mult">{reward}× Extension</span>
                            <div className="scd-mm-ext-prices">
                              <span className="scd-mm-price">₹{price.toFixed(2)}</span>
                              <span className="scd-mm-pct" style={{ color: MM_COLORS[reward] }}>+{pct}%</span>
                            </div>
                          </div>
                          <div className={`scd-mm-switch${on ? ' on' : ''}`} style={on ? { background: MM_COLORS[reward] } : {}}>
                            <div className="scd-mm-switch-thumb" />
                          </div>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <div className="scd-mm-unavailable">
                    Support &amp; resistance levels not available for this stock yet.
                  </div>
                )}

                  <div className="scd-mm-disclaimer">
                    Educational reference only. Not investment advice.
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {drawings.length === 0 && stock.candles?.length ? (
          <div className="scd-no-drawings">No analyst drawings for this stock yet.</div>
        ) : null}
      </div>
    </div>
  );
};

export default StockChartWithDrawings;
export type { Drawing };
